import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Plus, Trash2 } from "lucide-react";

const SPORTS = [
    "Grassroots Football",
    "Amateur Football",
    "Youth Football U14-U19",
    "Futsal",
    "Mini Football",
    "Padel",
    "Basketball",
    "Tennis",
];

const REQ_MARK = <span className="text-destructive ml-0.5" aria-hidden="true">*</span>;

function fieldClass(hasError) {
    return `h-12 rounded-md transition-colors ${hasError ? "bg-red-50 border-red-300 focus-visible:ring-red-400" : ""}`;
}

export default function Register() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [form, setForm] = useState({ name: "", surname: "", email: "", password: "", mobile: "", birth_year: "", location: "" });
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [hasExperience, setHasExperience] = useState(true);
    const [sports, setSports] = useState([]);
    const [interestedSports, setInterestedSports] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);

    const update = (k) => (e) => {
        setForm({ ...form, [k]: e.target.value });
        if (errors[k]) setErrors((prev) => ({ ...prev, [k]: false }));
    };

    const addSport = () => setSports([...sports, { sport: "", years: "" }]);
    const removeSport = (i) => setSports(sports.filter((_, idx) => idx !== i));
    const updateSport = (i, key, val) => {
        const next = [...sports];
        next[i] = { ...next[i], [key]: val };
        setSports(next);
    };


    const onSubmit = async (e) => {
        e.preventDefault();
        const trimmed = {
            name: form.name.trim(),
            surname: form.surname.trim(),
            email: form.email.trim(),
            mobile: form.mobile.trim(),
            password: form.password,
            confirmPassword,
            birth_year: form.birth_year.trim ? form.birth_year.trim() : String(form.birth_year || "").trim(),
            location: form.location.trim(),
        };
        const nextErrors = {
            name: !trimmed.name,
            surname: !trimmed.surname,
            email: !trimmed.email,
            mobile: !trimmed.mobile,
            password: !trimmed.password,
            confirmPassword: !trimmed.confirmPassword,
            birth_year: !trimmed.birth_year,
            location: !trimmed.location,
        };
        const anyMissing = Object.values(nextErrors).some(Boolean);
        if (anyMissing) {
            setErrors(nextErrors);
            toast.error(t("common.requiredFields"));
            return;
        }
        if (form.password !== confirmPassword) {
            setErrors({ password: true, confirmPassword: true });
            toast.error(t("common.passwordsDoNotMatch"));
            return;
        }
        const currentYear = new Date().getFullYear();
        const yearNum = parseInt(trimmed.birth_year, 10);
        if (Number.isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
            setErrors({ birth_year: true });
            toast.error(t("profile.yearRangeError", { max: currentYear }));
            return;
        }
        if (hasExperience) {
            const seen = new Set();
            for (const s of sports) {
                if (!s.sport) { toast.error(t("profile.selectSportError")); return; }
                if (seen.has(s.sport)) { toast.error(t("profile.duplicateSport", { name: s.sport })); return; }
                seen.add(s.sport);
            }
        }
        setErrors({});
        setLoading(true);
        try {
            const cleanSports = sports.map((s) => ({ sport: s.sport, years: String(s.years ?? "").trim() }));
            const payload = {
                ...form,
                birth_year: yearNum,
                has_experience: hasExperience,
                sports: hasExperience ? cleanSports : [],
                interested_sports: hasExperience ? "" : interestedSports.trim(),
            };
            const { data } = await api.post("/auth/register", payload);
            setSuccess(data);
            toast.success(t("register.submitSuccess"));
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8 relative" data-testid="register-success-screen">
                <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
                <div className="max-w-md w-full border border-border rounded-md bg-white p-10 space-y-6">
                    <div className="w-12 h-12 rounded-md bg-accent flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <div className="label-mono mb-2">{t("register.awaitingApproval")}</div>
                        <h1 className="font-heading text-2xl font-semibold">{t("register.submittedTitle")}</h1>
                        <p className="text-muted-foreground mt-2 text-sm">
                            {t("register.submittedBody", { name: form.name, email: form.email })}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Button onClick={() => navigate("/login")} variant="outline" className="flex-1 h-11" data-testid="register-go-login-btn">{t("register.goToLogin")}</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-8 relative">
            <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
            <div className="flex items-center justify-center w-full">
                <form onSubmit={onSubmit} className="w-full max-w-md space-y-8" data-testid="register-form" noValidate>
                    <Link to="/" className="flex items-center gap-1" data-testid="register-brand">
                        <img src="https://customer-assets.emergentagent.com/job_schedule-sync-109/artifacts/0i7pk9q1_refpairlogo_trans.png" alt="RefPair" className="w-14 h-14 object-contain" />
                        <span className="font-heading text-xl font-semibold tracking-tight">RefPair<span className="text-muted-foreground font-normal text-[0.72em] tracking-tighter -ml-[1px]">.com</span></span>
                    </Link>
                    <div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("register.title")}</h1>
                        <p className="text-muted-foreground mt-2">{t("register.subtitle")}</p>
                    </div>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">{t("common.username")}{REQ_MARK}</Label>
                            <Input id="name" minLength={3} value={form.name} onChange={update("name")}
                                aria-invalid={errors.name ? "true" : "false"}
                                className={fieldClass(errors.name)} data-testid="register-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="surname">{t("common.surname")}{REQ_MARK}</Label>
                            <Input id="surname" maxLength={80} value={form.surname} onChange={update("surname")}
                                aria-invalid={errors.surname ? "true" : "false"}
                                className={fieldClass(errors.surname)} data-testid="register-surname-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">{t("common.email")}{REQ_MARK}</Label>
                            <Input id="email" type="email" value={form.email} onChange={update("email")}
                                aria-invalid={errors.email ? "true" : "false"}
                                className={fieldClass(errors.email)} data-testid="register-email-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="mobile">{t("common.mobileNumber")}{REQ_MARK}</Label>
                            <Input id="mobile" value={form.mobile} onChange={update("mobile")}
                                aria-invalid={errors.mobile ? "true" : "false"}
                                className={fieldClass(errors.mobile)} data-testid="register-mobile-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">{t("common.password")}{REQ_MARK}</Label>
                            <Input id="password" type="password" minLength={6} value={form.password} onChange={update("password")}
                                aria-invalid={errors.password ? "true" : "false"}
                                className={fieldClass(errors.password)} data-testid="register-password-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm-password">{t("register.confirmPassword")}{REQ_MARK}</Label>
                            <Input id="confirm-password" type="password" minLength={6} value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: false }));
                                }}
                                aria-invalid={errors.confirmPassword ? "true" : "false"}
                                className={fieldClass(errors.confirmPassword)} data-testid="register-confirm-password-input" />
                            {confirmPassword && form.password !== confirmPassword && (
                                <p className="text-xs text-destructive mt-1" data-testid="register-password-mismatch">{t("common.passwordsDoNotMatch")}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="register-birth-year">{t("profile.birthYear")}{REQ_MARK}</Label>
                            <Input id="register-birth-year" type="number" min="1900" max={new Date().getFullYear()}
                                placeholder={t("profile.birthYearPlaceholder")}
                                value={form.birth_year} onChange={update("birth_year")}
                                aria-invalid={errors.birth_year ? "true" : "false"}
                                className={`${fieldClass(errors.birth_year)} mono`} data-testid="register-birth-year-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="register-location">{t("profile.location")}{REQ_MARK}</Label>
                            <Input id="register-location"
                                placeholder={t("profile.locationPlaceholder")}
                                value={form.location} onChange={update("location")}
                                maxLength={120}
                                aria-invalid={errors.location ? "true" : "false"}
                                className={fieldClass(errors.location)} data-testid="register-location-input" />
                        </div>

                        <div className="space-y-3 pt-2" data-testid="register-experience-block">
                            <div className="flex items-center justify-between gap-3 select-none">
                                <Label htmlFor="register-experience-toggle" className="text-sm font-medium">{t("profile.experienceLabel")}</Label>
                                <div className="flex items-center gap-3">
                                    <span className={`text-xs mono ${!hasExperience ? "text-primary font-semibold" : "text-muted-foreground"}`}>{t("profile.experienceNo")}</span>
                                    <Switch
                                        id="register-experience-toggle"
                                        checked={hasExperience}
                                        onCheckedChange={setHasExperience}
                                        className="h-7 w-[3.25rem] data-[state=checked]:[&>span]:translate-x-6 [&>span]:h-6 [&>span]:w-6"
                                        data-testid="register-experience-toggle"
                                    />
                                    <span className={`text-xs mono ${hasExperience ? "text-primary font-semibold" : "text-muted-foreground"}`}>{t("profile.experienceYes")}</span>
                                </div>
                            </div>

                            {hasExperience ? (
                                <div className="space-y-3" data-testid="register-sports-block">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs">{t("profile.sportsSection")}</Label>
                                        <Button type="button" variant="outline" size="sm" onClick={addSport} className="rounded-md h-8" data-testid="register-add-sport-btn">
                                            <Plus className="w-3.5 h-3.5 mr-1" /> {t("profile.addSport")}
                                        </Button>
                                    </div>
                                    {sports.length === 0 ? (
                                        <p className="text-xs text-muted-foreground py-3 text-center border border-dashed border-border rounded-md" data-testid="register-sports-empty">
                                            {t("profile.noSports")}
                                        </p>
                                    ) : (
                                        <ul className="space-y-2" data-testid="register-sports-list">
                                            {sports.map((s, i) => (
                                                <li key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`register-sport-row-${i}`}>
                                                    <div className="col-span-7">
                                                        <Select value={s.sport} onValueChange={(v) => updateSport(i, "sport", v)}>
                                                            <SelectTrigger className="h-10 rounded-md" data-testid={`register-sport-select-${i}`}>
                                                                <SelectValue placeholder={t("profile.selectSportPlaceholder")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {SPORTS.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="col-span-4">
                                                        <Input type="text"
                                                            value={s.years}
                                                            placeholder={t("profile.yearsPlaceholder")}
                                                            maxLength={40}
                                                            onChange={(e) => updateSport(i, "years", e.target.value)}
                                                            className="h-10 rounded-md"
                                                            data-testid={`register-sport-years-${i}`} />
                                                    </div>
                                                    <div className="col-span-1">
                                                        <Button type="button" variant="ghost" size="icon" onClick={() => removeSport(i)}
                                                            className="h-10 w-10 text-muted-foreground hover:text-destructive"
                                                            data-testid={`register-sport-delete-${i}`}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-2" data-testid="register-interested-sports-block">
                                    <Label htmlFor="register-interested-sports">{t("profile.interestedSportsLabel")}</Label>
                                    <Textarea
                                        id="register-interested-sports"
                                        value={interestedSports}
                                        onChange={(e) => setInterestedSports(e.target.value)}
                                        rows={4}
                                        maxLength={1000}
                                        placeholder={t("profile.interestedSportsPlaceholder")}
                                        className="rounded-md min-h-[120px]"
                                        data-testid="register-interested-sports-input"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <Button type="submit" disabled={loading}
                        className="w-full h-12 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="register-submit-button">
                        {loading ? t("common.creatingAccount") : t("common.createAccount")}
                    </Button>
                    <div className="text-sm text-muted-foreground text-center">
                        {t("register.alreadyAccount")} <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">{t("common.signIn")}</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
